import React, { type ReactElement } from 'react';
import classNames from 'classnames';
import Markdown from 'markdown-to-jsx';
import {
  Details,
  type DetailsProps,
} from '../../../../stories/Molecules/Details/Details';
import styles from './CollapsibleTextGroup.module.scss';
import { CollapsibleTextGroup as CollapsibleTextGroupData } from '../ComponentTypes';
import Section from '../../Section/Section';

interface CollapsibleTextGroupProps {
  addItemWrapper?: ReactElement;
  component: CollapsibleTextGroupData;
  defaultOpen?: DetailsProps['defaultOpen'];
  detailContentWrapper?: (id: string) => ReactElement;
  detailSummaryWrapper?: (id: string) => ReactElement;
  disableToggle?: DetailsProps['disableToggle'];
  removeItemWrapper?: (id: string) => ReactElement;
  textWrapper?: ReactElement;
}

const CollapsibleTextGroup: React.FC<CollapsibleTextGroupProps> = ({
  addItemWrapper,
  component,
  defaultOpen,
  detailContentWrapper,
  detailSummaryWrapper,
  disableToggle,
  removeItemWrapper,
  textWrapper,
}) => {
  if (!detailContentWrapper && !component.isPublished) {
    return null;
  }

  const details = !detailContentWrapper
    ? component.details.filter((detail) => detail.summary.length > 0)
    : component.details;

  return (
    <Section
      isXl
      isMtXl
      hasBgDarker
      hasBgPattern
    >
      <div
        className={classNames(
          styles.CollapsibleTextGroup,
          'seo',
          'seo--new',
          'lh-lg',
        )}
      >
        {textWrapper ? (
          React.cloneElement(textWrapper, {}, component.text)
        ) : (
          <Markdown className="seo__text">{component.text}</Markdown>
        )}
        {details.map((detail) => (
          <Details
            key={detail.id}
            defaultOpen={defaultOpen}
            disableToggle={disableToggle}
          >
            <Details.Summary>
              {detailSummaryWrapper ? (
                React.cloneElement(
                  detailSummaryWrapper(detail.id) ?? <>{}</>,
                  {},
                  <h2 className="h5">{detail.summary}</h2>,
                )
              ) : (
                <h2 className="h5">{detail.summary}</h2>
              )}
              {removeItemWrapper && removeItemWrapper(detail.id)}
            </Details.Summary>
            <Details.Content>
              <div className={styles.CollapsibleTextGroup__content}>
                {detailContentWrapper ? (
                  React.cloneElement(
                    detailContentWrapper(detail.id),
                    {},
                    detail.content,
                  )
                ) : (
                  <Markdown className="seo__text">{detail.content}</Markdown>
                )}
              </div>
            </Details.Content>
          </Details>
        ))}
      </div>
      {addItemWrapper && addItemWrapper}
    </Section>
  );
};

export default CollapsibleTextGroup;
